/**
 * Technical-indicator library.
 *
 * Every function is pure: same candles in → same numbers out, no I/O, no
 * clocks, no React. The Chart component renders whatever indicators the user
 * has toggled on by mapping `id → compute(candles)` and dropping the result
 * into either the price pane (overlays) or a stacked sub-pane (oscillators).
 *
 * Math notes:
 *  - All inputs are arrays of {open, high, low, close, volume}.
 *  - All outputs are numeric arrays the same length as the input.
 *  - Where a window can't be filled (i < period - 1) the value is NaN.
 *  - Multi-line indicators return an object keyed by line name.
 */
export interface Candle { t: number; open: number; high: number; low: number; close: number; volume: number; }

// ---------- moving averages ----------
export function sma(xs: number[], n: number): number[] {
  const out: number[] = new Array(xs.length).fill(NaN);
  let sum = 0;
  for (let i = 0; i < xs.length; i++) {
    sum += xs[i] ?? 0;
    if (i >= n) sum -= xs[i - n] ?? 0;
    if (i >= n - 1) out[i] = sum / n;
  }
  return out;
}

export function ema(xs: number[], n: number): number[] {
  const k = 2 / (n + 1);
  const out: number[] = [];
  let prev = xs[0] ?? 0;
  for (let i = 0; i < xs.length; i++) {
    const v = xs[i] ?? prev;
    const e = i === 0 ? v : v * k + prev * (1 - k);
    out.push(e); prev = e;
  }
  return out;
}

export function dema(xs: number[], n: number): number[] {
  const e1 = ema(xs, n);
  const e2 = ema(e1, n);
  return e1.map((v, i) => 2 * v - (e2[i] ?? v));
}

export function tema(xs: number[], n: number): number[] {
  const e1 = ema(xs, n);
  const e2 = ema(e1, n);
  const e3 = ema(e2, n);
  return e1.map((v, i) => 3 * v - 3 * (e2[i] ?? v) + (e3[i] ?? v));
}

/**
 * McGinley Dynamic — adaptive MA that adjusts faster in volatile markets.
 *  MD_t = MD_{t-1} + (Close_t - MD_{t-1}) / (n * (Close_t / MD_{t-1})^4)
 */
export function mcginley(xs: number[], n: number): number[] {
  const out: number[] = [];
  let md = xs[0] ?? 0;
  for (let i = 0; i < xs.length; i++) {
    const c = xs[i] ?? md;
    if (i === 0 || md === 0) { out.push(c); md = c; continue; }
    const ratio = c / md;
    md = md + (c - md) / (n * Math.pow(ratio, 4));
    out.push(md);
  }
  return out;
}

// ---------- bands & channels ----------
export function bollinger(xs: number[], n: number, k: number): { upper: number[]; lower: number[]; mid: number[] } {
  const mid = sma(xs, n);
  const upper: number[] = []; const lower: number[] = [];
  for (let i = 0; i < xs.length; i++) {
    if (i < n - 1) { upper.push(NaN); lower.push(NaN); continue; }
    const slice = xs.slice(i - n + 1, i + 1);
    const m = mid[i] ?? 0;
    const variance = slice.reduce((s, v) => s + (v - m) ** 2, 0) / slice.length;
    const sd = Math.sqrt(variance);
    upper.push(m + k * sd); lower.push(m - k * sd);
  }
  return { upper, lower, mid };
}

export function priceChannel(candles: Candle[], n: number): { upper: number[]; lower: number[] } {
  const upper: number[] = []; const lower: number[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < n - 1) { upper.push(NaN); lower.push(NaN); continue; }
    let hi = -Infinity; let lo = Infinity;
    for (let j = i - n + 1; j <= i; j++) {
      hi = Math.max(hi, candles[j]!.high);
      lo = Math.min(lo, candles[j]!.low);
    }
    upper.push(hi); lower.push(lo);
  }
  return { upper, lower };
}

/**
 * Linear regression line over the trailing `n` closes — returns the
 * regression value at each bar position.
 */
export function linearRegression(xs: number[], n: number): number[] {
  const out: number[] = new Array(xs.length).fill(NaN);
  for (let i = n - 1; i < xs.length; i++) {
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let k = 0; k < n; k++) {
      const x = k;
      const y = xs[i - n + 1 + k] ?? 0;
      sumX += x; sumY += y; sumXY += x * y; sumXX += x * x;
    }
    const denom = (n * sumXX - sumX * sumX) || 1;
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    out[i] = slope * (n - 1) + intercept;
  }
  return out;
}

export function vwap(candles: Candle[]): number[] {
  let cumPV = 0, cumV = 0;
  return candles.map((d) => {
    const tp = (d.high + d.low + d.close) / 3;
    cumPV += tp * d.volume; cumV += d.volume;
    return cumV === 0 ? tp : cumPV / cumV;
  });
}

// ---------- oscillators ----------
export function rsi(closes: number[], n: number): number[] {
  const out: number[] = new Array(closes.length).fill(NaN);
  let gainSum = 0, lossSum = 0;
  for (let i = 1; i <= n && i < closes.length; i++) {
    const d = (closes[i] ?? 0) - (closes[i - 1] ?? 0);
    if (d > 0) gainSum += d; else lossSum -= d;
  }
  let avgGain = gainSum / n; let avgLoss = lossSum / n;
  if (n < closes.length) {
    out[n] = 100 - (100 / (1 + (avgLoss === 0 ? 100 : avgGain / avgLoss)));
  }
  for (let i = n + 1; i < closes.length; i++) {
    const d = (closes[i] ?? 0) - (closes[i - 1] ?? 0);
    const g = d > 0 ? d : 0; const l = d < 0 ? -d : 0;
    avgGain = (avgGain * (n - 1) + g) / n;
    avgLoss = (avgLoss * (n - 1) + l) / n;
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    out[i] = 100 - 100 / (1 + rs);
  }
  return out;
}

export function macd(closes: number[], fast = 12, slow = 26, signal = 9):
  { macd: number[]; signal: number[]; histogram: number[] } {
  const ef = ema(closes, fast); const es = ema(closes, slow);
  const m = ef.map((v, i) => v - (es[i] ?? v));
  const s = ema(m, signal);
  const h = m.map((v, i) => v - (s[i] ?? v));
  return { macd: m, signal: s, histogram: h };
}

export function stochastic(candles: Candle[], n: number, smoothK = 3, smoothD = 3):
  { k: number[]; d: number[] } {
  const raw: number[] = new Array(candles.length).fill(NaN);
  for (let i = n - 1; i < candles.length; i++) {
    let hi = -Infinity, lo = Infinity;
    for (let j = i - n + 1; j <= i; j++) {
      hi = Math.max(hi, candles[j]!.high);
      lo = Math.min(lo, candles[j]!.low);
    }
    const c = candles[i]!.close;
    raw[i] = hi === lo ? 50 : ((c - lo) / (hi - lo)) * 100;
  }
  const k = sma(raw, smoothK);
  const d = sma(k, smoothD);
  return { k, d };
}

export function cci(candles: Candle[], n: number): number[] {
  const tp = candles.map((d) => (d.high + d.low + d.close) / 3);
  const tpSma = sma(tp, n);
  const out: number[] = new Array(candles.length).fill(NaN);
  for (let i = n - 1; i < candles.length; i++) {
    let mad = 0;
    for (let j = i - n + 1; j <= i; j++) mad += Math.abs((tp[j] ?? 0) - (tpSma[i] ?? 0));
    mad /= n;
    out[i] = mad === 0 ? 0 : ((tp[i] ?? 0) - (tpSma[i] ?? 0)) / (0.015 * mad);
  }
  return out;
}

/**
 * ADX (Average Directional Index) with +DI / -DI lines.
 * Standard Wilder smoothing.
 */
export function adx(candles: Candle[], n: number): { adx: number[]; pdi: number[]; mdi: number[] } {
  const len = candles.length;
  const tr: number[] = new Array(len).fill(0);
  const pDM: number[] = new Array(len).fill(0);
  const mDM: number[] = new Array(len).fill(0);
  for (let i = 1; i < len; i++) {
    const a = candles[i]!, b = candles[i - 1]!;
    tr[i] = Math.max(a.high - a.low, Math.abs(a.high - b.close), Math.abs(a.low - b.close));
    const upMove = a.high - b.high; const downMove = b.low - a.low;
    pDM[i] = (upMove > downMove && upMove > 0) ? upMove : 0;
    mDM[i] = (downMove > upMove && downMove > 0) ? downMove : 0;
  }
  // Wilder smoothing
  const wsmooth = (xs: number[]) => {
    const out: number[] = new Array(len).fill(NaN);
    let acc = 0;
    for (let i = 1; i <= n && i < len; i++) acc += xs[i] ?? 0;
    if (n < len) out[n] = acc;
    for (let i = n + 1; i < len; i++) {
      acc = acc - acc / n + (xs[i] ?? 0);
      out[i] = acc;
    }
    return out;
  };
  const trN = wsmooth(tr); const pDMN = wsmooth(pDM); const mDMN = wsmooth(mDM);
  const pdi: number[] = new Array(len).fill(NaN);
  const mdi: number[] = new Array(len).fill(NaN);
  const dx: number[] = new Array(len).fill(NaN);
  for (let i = n; i < len; i++) {
    const tr_ = trN[i] || 1;
    pdi[i] = ((pDMN[i] ?? 0) / tr_) * 100;
    mdi[i] = ((mDMN[i] ?? 0) / tr_) * 100;
    const sum = (pdi[i] ?? 0) + (mdi[i] ?? 0);
    dx[i] = sum === 0 ? 0 : (Math.abs((pdi[i] ?? 0) - (mdi[i] ?? 0)) / sum) * 100;
  }
  // ADX = Wilder-smoothed DX
  const adxOut: number[] = new Array(len).fill(NaN);
  let acc = 0;
  for (let i = n; i < n + n && i < len; i++) acc += dx[i] ?? 0;
  if (n + n - 1 < len) adxOut[n + n - 1] = acc / n;
  for (let i = n + n; i < len; i++) {
    adxOut[i] = ((adxOut[i - 1] ?? 0) * (n - 1) + (dx[i] ?? 0)) / n;
  }
  return { adx: adxOut, pdi, mdi };
}

/** Awesome Oscillator: SMA(median, 5) - SMA(median, 34). */
export function awesomeOscillator(candles: Candle[]): number[] {
  const median = candles.map((d) => (d.high + d.low) / 2);
  const a = sma(median, 5); const b = sma(median, 34);
  return a.map((v, i) => v - (b[i] ?? v));
}

// ---------- volume / accumulation ----------
export function moneyFlowIndex(candles: Candle[], n: number): number[] {
  const tp = candles.map((d) => (d.high + d.low + d.close) / 3);
  const flow = candles.map((d, i) => tp[i]! * d.volume);
  const out: number[] = new Array(candles.length).fill(NaN);
  for (let i = n; i < candles.length; i++) {
    let pos = 0, neg = 0;
    for (let j = i - n + 1; j <= i; j++) {
      if ((tp[j] ?? 0) > (tp[j - 1] ?? 0)) pos += flow[j] ?? 0;
      else if ((tp[j] ?? 0) < (tp[j - 1] ?? 0)) neg += flow[j] ?? 0;
    }
    const ratio = neg === 0 ? 100 : pos / neg;
    out[i] = 100 - 100 / (1 + ratio);
  }
  return out;
}

/** Accumulation/Distribution Line — running cumulative of CMF multiplier × volume. */
export function accDistribution(candles: Candle[]): number[] {
  let acc = 0;
  return candles.map((d) => {
    const range = d.high - d.low;
    const mfm = range === 0 ? 0 : ((d.close - d.low) - (d.high - d.close)) / range;
    acc += mfm * d.volume;
    return acc;
  });
}

export function chaikinMoneyFlow(candles: Candle[], n: number): number[] {
  const out: number[] = new Array(candles.length).fill(NaN);
  for (let i = n - 1; i < candles.length; i++) {
    let mfvSum = 0, vSum = 0;
    for (let j = i - n + 1; j <= i; j++) {
      const d = candles[j]!;
      const range = d.high - d.low;
      const mfm = range === 0 ? 0 : ((d.close - d.low) - (d.high - d.close)) / range;
      mfvSum += mfm * d.volume;
      vSum += d.volume;
    }
    out[i] = vSum === 0 ? 0 : mfvSum / vSum;
  }
  return out;
}

export function chaikinOscillator(candles: Candle[]): number[] {
  const ad = accDistribution(candles);
  const e3 = ema(ad, 3); const e10 = ema(ad, 10);
  return e3.map((v, i) => v - (e10[i] ?? v));
}

export function forceIndex(candles: Candle[], n = 13): number[] {
  const raw: number[] = candles.map((d, i) => i === 0 ? 0 : (d.close - (candles[i - 1]?.close ?? d.close)) * d.volume);
  return ema(raw, n);
}

export function onBalanceVolume(candles: Candle[]): number[] {
  let obv = 0;
  return candles.map((d, i) => {
    if (i === 0) return 0;
    const prev = candles[i - 1]!.close;
    if (d.close > prev) obv += d.volume;
    else if (d.close < prev) obv -= d.volume;
    return obv;
  });
}

export function negVolumeIndex(candles: Candle[]): number[] {
  let nvi = 1000;
  const out: number[] = new Array(candles.length).fill(nvi);
  for (let i = 1; i < candles.length; i++) {
    const a = candles[i]!, b = candles[i - 1]!;
    if (a.volume < b.volume) {
      const pct = b.close === 0 ? 0 : (a.close - b.close) / b.close;
      nvi = nvi + nvi * pct;
    }
    out[i] = nvi;
  }
  return out;
}

export function posVolumeIndex(candles: Candle[]): number[] {
  let pvi = 1000;
  const out: number[] = new Array(candles.length).fill(pvi);
  for (let i = 1; i < candles.length; i++) {
    const a = candles[i]!, b = candles[i - 1]!;
    if (a.volume > b.volume) {
      const pct = b.close === 0 ? 0 : (a.close - b.close) / b.close;
      pvi = pvi + pvi * pct;
    }
    out[i] = pvi;
  }
  return out;
}

export function aroon(candles: Candle[], n: number): { up: number[]; down: number[] } {
  const up: number[] = new Array(candles.length).fill(NaN);
  const down: number[] = new Array(candles.length).fill(NaN);
  for (let i = n; i < candles.length; i++) {
    let hiIdx = i, loIdx = i;
    for (let j = i - n; j <= i; j++) {
      if ((candles[j]?.high ?? -Infinity) >= (candles[hiIdx]?.high ?? -Infinity)) hiIdx = j;
      if ((candles[j]?.low  ?? Infinity)  <= (candles[loIdx]?.low  ?? Infinity))  loIdx = j;
    }
    up[i] = ((n - (i - hiIdx)) / n) * 100;
    down[i] = ((n - (i - loIdx)) / n) * 100;
  }
  return { up, down };
}

export function volumeAvg(candles: Candle[], n: number): { volume: number[]; avg: number[] } {
  const v = candles.map((d) => d.volume);
  return { volume: v, avg: sma(v, n) };
}
